import Router from 'next/router';
import axios from 'axios';

import { PATHS, VERIFAST_API_URL } from '../const';
import { sessionStorageController } from '../services/storages';

import type { InsuranceForm } from '../types';
import type { VerifyInsuranceFormRequest } from './request-types';
import type { GetUserMeResponse } from './response-types';
import type { VehicleType } from '@autopass-web/types';

const axiosInstance = axios.create({
  baseURL: VERIFAST_API_URL,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorageController.getToken();

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (err) => {
    if (err.response.status === 401) {
      sessionStorage.clear();
      localStorage.clear();
      Router.replace(PATHS.INDEX);
    }

    return Promise.reject(err);
  },
);

class Client {
  signInWithLine = async ({ idToken }: { idToken: string }) =>
    axiosInstance.post<{ data: { jwt_token: string } }>(
      '/verifast/third-party-login/line',
      { id_token: idToken },
    );
  fetchUserMe = async () =>
    axiosInstance.get<GetUserMeResponse>('/verifast/me');
  sendInsuranceFormResource = async ({
    plateNumber,
    vehicleType,
    vehicleLicenseImage,
    driverLicenseBackImage,
    driverLicenseFrontImage,
  }: {
    plateNumber: string;
    vehicleType: VehicleType;
    vehicleLicenseImage: string;
    driverLicenseFrontImage: string;
    driverLicenseBackImage: string;
  }) =>
    axiosInstance.post<void>('/verifast/insurance-forms', {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      vehicle_license_image: vehicleLicenseImage,
      driver_license_front_image: driverLicenseFrontImage,
      driver_license_back_image: driverLicenseBackImage,
    });
  fetchInsuranceForms = async () =>
    axiosInstance.get<{ data: InsuranceForm[] }>('/verifast/insurance-forms');
  verifyInsuranceForm = async ({
    vehicleType,
    formId,
    vehicleLicenseName,
    vehicleBrand,
    driverLicenseName,
    cellphoneNumber,
    email,
    personalId,
    birthDate,
    residentialAddress,
    vehicleLicenseIssueDate,
    permanentAddress,
    engineDisplacement,
    manufactureDate,
    vehicleSeat,
  }: VerifyInsuranceFormRequest) =>
    axiosInstance.post<void>(`/verifast/insurance-forms/${formId}/submit`, {
      vehicle_type: vehicleType,
      vehicle_license_name: vehicleLicenseName,
      vehicle_brand: vehicleBrand,
      driver_license_name: driverLicenseName,
      cellphone_number: cellphoneNumber,
      email,
      personal_id: personalId,
      birth_date: birthDate,
      residential_address: residentialAddress,
      vehicle_license_issue_date: vehicleLicenseIssueDate,
      permanent_address: permanentAddress,
      engine_displacement: engineDisplacement,
      manufacture_date: manufactureDate,
      vehicle_seat: vehicleSeat,
    });
}

const client = new Client();

export default client;
